import React, { useEffect, useState, useCallback } from 'react';
import { identitesDuCompte, ensembleAutorise } from '../../utils/accountMatching';
import ReactDOM from 'react-dom';
import { DndContext, DragOverlay, pointerWithin, rectIntersection, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { Plug, Package, Globe, Users, Plus, Wand2, X, Folder } from 'lucide-react';

// Logos pour DragOverlay et modal connecteurs
import googleAdsLogo from '../../logos/google-ads.png';
import microsoftAdsLogo from '../../logos/microsoft-ads.png';
import metaAdsLogo from '../../logos/meta-ads.png';
import matomoLogo from '../../logos/matomo.png';
import pianoLogo from '../../logos/piano-analytics.png';
import googleSearchConsoleLogo from '../../logos/google-search-console.png';
import tagManagerLogo from '../../logos/google-tag-manager.png';
import pinterestAdsLogo from '../../logos/pinterest-ads.png';
import tiktokAdsLogo from '../../logos/tiktok-ads.png';
import chatgptAdsLogo from '../../logos/chatgpt-ads.png';
import awinLogo from '../../logos/awin.png';
import prestashopLogo from '../../logos/prestashop.png';
import { ReactComponent as GoogleAnalyticsLogo } from '../../logos/google-analytics.svg';

import { KanbanProvider, useKanban } from './KanbanContext';
import KanbanColumn from './KanbanColumn';
import ConnectorCard from './cards/ConnectorCard';
import AccountCard from './cards/AccountCard';
import SiteCard from './cards/SiteCard';
import ClientCard from './cards/ClientCard';
import ImportAccountsModal from './modals/ImportAccountsModal';
import ClientAccessModal from './modals/ClientAccessModal';
import CreateSiteModal from './modals/CreateSiteModal';
import CreateClientModal from './modals/CreateClientModal';
import SetupWizard from './wizard/SetupWizard';

// Providers où chaque connecteur = un compte (regroupés dans la vue)
const MULTI_CONNECTOR_PROVIDERS = ['MATOMO', 'PIANO_ANALYTICS'];

// Config des labels de providers
const providerLabels = {
  GOOGLE_ADS: 'Google Ads',
  GOOGLE_SHEETS: 'Google Sheets',
  GOOGLE_MERCHANT_API: 'Google Merchant Center',
  GOOGLE_ANALYTICS: 'Google Analytics',
  GOOGLE_SEARCH_CONSOLE: 'Google Search Console',
  GOOGLE_TAG_MANAGER: 'Google Tag Manager',
  MICROSOFT_ADS: 'Microsoft Ads',
  FACEBOOK_ADS: 'Meta Ads',
  MATOMO: 'Matomo',
  PIANO_ANALYTICS: 'Piano Analytics',
  PINTEREST_ADS: 'Pinterest Ads',
  TIKTOK_ADS: 'TikTok Ads',
  OPENAI_ADS: 'ChatGPT Ads',
  AWIN: 'Awin',
  PRESTASHOP: 'PrestaShop',
  OPEN_METEO: 'Open-Meteo'
};

const KanbanContent = ({ token, onConnectorConnect, onConnectorDisconnect, onDataChange }) => {
  const {
    connectors,
    accounts,
    sites,
    clients,
    loading,
    error,
    searchQueries,
    selection,
    loadData,
    setSearchQuery,
    getFilteredItems,
    totauxDeColonne,
    rattachement,
    associateAccountToSite,
    dissociateAccountFromSite,
    assignSiteToClient,
    unassignSiteFromClient,
    selectItem,
    clearSelection
  } = useKanban();

  const [activeItem, setActiveItem] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [selectedClientForAccess, setSelectedClientForAccess] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [createSiteModalOpen, setCreateSiteModalOpen] = useState(false);
  const [createClientModalOpen, setCreateClientModalOpen] = useState(false);
  const [addConnectorModalOpen, setAddConnectorModalOpen] = useState(false);

  // Vérifier si au moins un connecteur est actif
  const hasActiveConnectors = connectors.filter(c => c.isActive).length > 0;

  // API URL pour les connexions OAuth
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  // Configurer les sensors pour le drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );

  // Collision detection personnalisée qui priorise les drop zones (cartes) sur les colonnes
  const customCollisionDetection = useCallback((args) => {
    // Utiliser rectIntersection pour une détection plus précise
    const collisions = rectIntersection(args);

    if (!collisions || collisions.length === 0) {
      return [];
    }

    // Trier pour prioriser site-drop-zone et client-drop-zone
    const sorted = [...collisions].sort((a, b) => {
      const aType = a.data?.droppableContainer?.data?.current?.type || '';
      const bType = b.data?.droppableContainer?.data?.current?.type || '';

      // Prioriser les drop zones spécifiques
      const aIsPriority = aType === 'site-drop-zone' || aType === 'client-drop-zone';
      const bIsPriority = bType === 'site-drop-zone' || bType === 'client-drop-zone';

      if (aIsPriority && !bIsPriority) return -1;
      if (!aIsPriority && bIsPriority) return 1;
      return 0;
    });

    return sorted;
  }, []);

  // Charger les données au montage
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fonction helper pour rafraîchir les données locales ET notifier le parent
  const handleDataRefresh = useCallback(async () => {
    // Attendre que les données locales soient chargées
    await loadData();
    // Petit délai pour laisser le temps au backend de finaliser
    await new Promise(resolve => setTimeout(resolve, 300));
    // Notifier le parent pour rafraîchir les sources disponibles
    if (onDataChange) onDataChange();
  }, [loadData, onDataChange]);

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveItem(active.data.current);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveItem(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    console.log('[DRAG] Drop:', {
      activeData,
      activeDataType: activeData?.type,
      overData,
      overId: over.id,
      overDataType: overData?.type
    });

    // Compte → Site (détection par overId ou overData.type)
    if (activeData?.type === 'account') {
      // Extraire le siteId depuis overId (format: site-drop-{siteId}) ou depuis overData
      let siteId = null;

      if (overData?.type === 'site-drop-zone') {
        siteId = overData.siteId;
      } else if (over.id && String(over.id).startsWith('site-drop-')) {
        // Fallback: extraire le siteId depuis l'overId
        siteId = String(over.id).replace('site-drop-', '');
      }

      if (siteId) {
        const brut = activeData.id || activeData.sourceId;
        // `activeData` **est** le compte glissé : le retrouver par identifiant rendait le premier
        // homonyme, et les comptes Matomo en avaient. On garde la recherche en repli, pour un
        // glisser qui ne porterait pas la charge complète.
        const account = activeData.provider
          ? activeData
          : accounts.find(a => (a.id || a.sourceId) === brut);

        /**
         * Matomo et Piano s'écrivent avec leur connexion, jamais avec le numéro de site nu :
         * ce numéro n'est unique que dans une instance, et l'écrire seul produisait un
         * rattachement qui désignait plusieurs comptes à la fois.
         */
        const { identifiant, caducs } = rattachement(account);
        const accountId = identifiant || brut;
        const site = sites.find(s => s.id === siteId);
        const remplaces = caducs ? caducs(site?.accountIds) : [];
        console.log('[DRAG] Association compte→site:', { accountId, siteId, remplaces });

        // Vérifier si le compte n'est pas déjà associé à ce site
        if (account?.siteId !== siteId) {
          try {
            // Le compte est passé en plus de son identifiant : celui-ci sert à écrire dans le
            // site, celui-là à retrouver la carte à rafraîchir.
            await associateAccountToSite(accountId, siteId, remplaces, account);
          } catch (err) {
            console.error('Erreur association:', err);
          }
        } else {
          console.log('[DRAG] Compte déjà associé à ce site');
        }
      } else if (overData?.type === 'column' && overData?.columnId === 'sites') {
        console.log('[DRAG] Compte droppé sur colonne Sites - dropper sur une carte site spécifique');
      }
    }

    // Site → Client (détection par overId ou overData.type)
    if (activeData.type === 'site') {
      let clientId = null;

      if (overData?.type === 'client-drop-zone') {
        clientId = overData.clientId;
      } else if (over.id && String(over.id).startsWith('client-drop-')) {
        // Fallback: extraire le clientId depuis l'overId
        clientId = String(over.id).replace('client-drop-', '');
      }

      if (clientId) {
        const siteId = activeData.id;
        console.log('[DRAG] Assignation site→client:', { siteId, clientId });

        const site = sites.find(s => s.id === siteId);
        if (site?.clientId !== clientId) {
          try {
            await assignSiteToClient(siteId, clientId);
          } catch (err) {
            console.error('Erreur assignation:', err);
          }
        } else {
          console.log('[DRAG] Site déjà assigné à ce client');
        }
      } else if (overData?.type === 'column' && overData?.columnId === 'clients') {
        console.log('[DRAG] Site droppé sur colonne Clients - dropper sur une carte client spécifique');
      }
    }
  };

  const handleDragCancel = () => {
    setActiveItem(null);
  };

  const handleManageAccess = (client) => {
    setSelectedClientForAccess(client);
    setAccessModalOpen(true);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        color: '#64748b'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ marginBottom: '1rem' }} />
          <p>Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        color: '#dc2626'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ marginBottom: '1rem' }}>Erreur: {error}</p>
          <button
            onClick={loadData}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: 'none',
              backgroundColor: '#1a1a1a',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const wizardButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '12px 24px',
    borderRadius: '0.5rem',
    border: 'none',
    backgroundColor: '#1e293b',
    color: 'white',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  };

  const clearFilterButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.875rem',
    borderRadius: '0.5rem',
    border: '1px solid #e5e0d5',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    fontSize: '0.8rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  // Obtenir le nom de l'élément sélectionné pour l'afficher
  const getSelectionLabel = () => {
    if (!selection.type || !selection.id) return null;
    switch (selection.type) {
      case 'client':
        return clients.find(c => c.id === selection.id)?.name || 'Client';
      case 'site':
        return sites.find(s => s.id === selection.id)?.name || 'Site';
      case 'account':
        const acc = accounts.find(a => a.id === selection.id || a.sourceId === selection.id);
        return acc?.name || 'Compte';
      case 'connector':
        const conn = connectors.find(c => c.id === selection.id);
        // Afficher le label du provider (ex: "Google Ads") au lieu de l'ID
        return providerLabels[conn?.provider] || conn?.provider || 'Connecteur';
      default:
        return null;
    }
  };

  // Grouper les connecteurs Matomo/Piano pour éviter les doublons
  const getGroupedConnectors = () => {
    const filtered = getFilteredItems('connectors');
    const standardConnectors = filtered.filter(c => !MULTI_CONNECTOR_PROVIDERS.includes(c.provider));

    // Créer des entrées groupées pour les multi-connector providers
    const groupedProviders = [];
    MULTI_CONNECTOR_PROVIDERS.forEach(provider => {
      const providerConnectors = filtered.filter(c => c.provider === provider);
      if (providerConnectors.length > 0) {
        groupedProviders.push({
          id: `group-${provider}`,
          provider: provider,
          isGroup: true,
          count: providerConnectors.length,
          connectors: providerConnectors,
          // Utiliser les infos du premier connecteur pour l'affichage
          connectionName: null,
          connectorEmail: null,
          isActive: providerConnectors.some(c => c.isActive)
        });
      }
    });

    return [...standardConnectors, ...groupedProviders];
  };

  /*
   * Les colonnes occupent la hauteur qui reste, et défilent à l'intérieur.
   *
   * `maxHeight: calc(100vh - 220px)` faisait dépasser la page : ces 220 pixels étaient une
   * estimation de l'en-tête, du fil d'Ariane et des marges, et elle tombait à côté — l'écran
   * défilait de quatre-vingt-dix pixels. La hauteur se déduit désormais du conteneur
   * (`flex: 1`), qui la tient de la zone de contenu : plus rien à estimer.
   */
  const columnsContainerStyle = {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    gap: '1rem',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: '1rem'
  };

  const footerButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.375rem',
    width: '100%',
    padding: '0.5rem',
    borderRadius: '0.375rem',
    border: '1px dashed #d5cfc5',
    backgroundColor: 'transparent',
    color: '#64748b',
    fontSize: '0.8rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {/* `kanban-page` sert de repère au CSS : c'est par lui que la zone de contenu sait
          qu'elle ne doit pas défiler sur cet écran. */}
      <div className="kanban-page">
        <div className="page-header">
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span><Folder size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} /> Gestionnaire</span>
          </h2>
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>

            {/* Bouton de filtre actif */}
            {selection.type && (
              <button
                style={clearFilterButtonStyle}
                onClick={clearSelection}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fde68a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fef3c7';
                }}
              >
                Filtre: {getSelectionLabel()}
                <X size={14} />
              </button>
            )}

            {hasActiveConnectors && (
              <button
                style={wizardButtonStyle}
                onClick={() => setWizardOpen(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0f172a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#1e293b';
                }}
              >
                <Wand2 size={18} />
                Nouveau
              </button>
            )}
          </div>
        </div>

        <div style={columnsContainerStyle}>
          {/* Colonne Connecteurs */}
          <KanbanColumn
            id="connectors"
            title="Connecteurs"
            icon={Plug}
            count={totauxDeColonne.connectors}
            items={getGroupedConnectors()}
            searchQuery={searchQueries.connectors}
            onSearchChange={(q) => setSearchQuery('connectors', q)}
            renderCard={(connector) => (
              <ConnectorCard
                key={connector.id}
                connector={connector}
                onConnect={onConnectorConnect}
                onDisconnect={onConnectorDisconnect}
                onClick={() => {
                  // Pour un groupe, sélectionner le premier connecteur du groupe
                  if (connector.isGroup && connector.connectors?.length > 0) {
                    selectItem('connector', connector.connectors[0].id);
                  } else {
                    selectItem('connector', connector.id);
                  }
                }}
                isSelected={
                  connector.isGroup
                    ? connector.connectors?.some(c => selection.type === 'connector' && selection.id === c.id)
                    : selection.type === 'connector' && selection.id === connector.id
                }
              />
            )}
            emptyMessage="Aucun connecteur"
            footerAction={
              <button
                style={footerButtonStyle}
                onClick={() => setAddConnectorModalOpen(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#1a1a1a';
                  e.currentTarget.style.color = '#1a1a1a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d5cfc5';
                  e.currentTarget.style.color = '#64748b';
                }}
              >
                <Plus size={14} />
                Connecteur
              </button>
            }
          />

          {/* Colonnes Comptes, Sites, Clients - uniquement si connecteurs actifs */}
          {hasActiveConnectors && (
            <>
              {/* Colonne Comptes */}
              <KanbanColumn
                id="accounts"
                title="Comptes"
                icon={Package}
                count={totauxDeColonne.accounts}
                items={getFilteredItems('accounts')}
                searchQuery={searchQueries.accounts}
                onSearchChange={(q) => setSearchQuery('accounts', q)}
                renderCard={(account) => (
                  <AccountCard
                    key={account.id || account.sourceId}
                    account={account}
                    // Défaire un rattachement demande le même identifiant que pour le poser,
                    // plus les écritures anciennes qui liaient encore ce compte : sans elles, un
                    // numéro nu resté en place aurait maintenu le lien.
                    onDissociate={(brut, siteId) => {
                      const { identifiant, caducs } = rattachement(account);
                      const site = sites.find(s => s.id === siteId);
                      /**
                       * Ce qui lie **réellement** ce compte à ce site, quelle que soit
                       * l'écriture employée à l'époque du rattachement.
                       *
                       * Retirer le seul identifiant qu'on écrirait aujourd'hui ne suffit pas :
                       * un compte Meta rattaché sous son identifiant de base, un compte Google
                       * Ads rattaché avec tirets, restaient liés après un clic qui semblait
                       * pourtant avoir fonctionné.
                       */
                      const identites = ensembleAutorise(identitesDuCompte(account));
                      const liens = (site?.accountIds || [])
                        .filter(a => identites.has(String(a)));

                      return dissociateAccountFromSite(
                        identifiant || brut,
                        siteId,
                        [...(caducs ? caducs(site?.accountIds) : []), ...liens],
                        account
                      );
                    }}
                    onClick={() => selectItem('account', account.id || account.sourceId)}
                    isSelected={selection.type === 'account' && (selection.id === account.id || selection.id === account.sourceId)}
                  />
                )}
                emptyMessage="Aucun compte importé"
                footerAction={
                  <button
                    style={footerButtonStyle}
                    onClick={() => setImportModalOpen(true)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.color = '#1a1a1a';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d5cfc5';
                      e.currentTarget.style.color = '#64748b';
                    }}
                  >
                    <Plus size={14} />
                    Importer
                  </button>
                }
              />

              {/* Colonne Sites */}
              <KanbanColumn
                id="sites"
                title="Sites"
                icon={Globe}
                count={totauxDeColonne.sites}
                items={getFilteredItems('sites')}
                searchQuery={searchQueries.sites}
                onSearchChange={(q) => setSearchQuery('sites', q)}
                droppable={true}
                accepts={['account']}
                renderCard={(site) => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    onUnassign={unassignSiteFromClient}
                    onClick={() => selectItem('site', site.id)}
                    isSelected={selection.type === 'site' && selection.id === site.id}
                  />
                )}
                emptyMessage="Aucun site"
                footerAction={
                  <button
                    style={footerButtonStyle}
                    onClick={() => setCreateSiteModalOpen(true)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.color = '#1a1a1a';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d5cfc5';
                      e.currentTarget.style.color = '#64748b';
                    }}
                  >
                    <Plus size={14} />
                    Nouveau
                  </button>
                }
              />

              {/* Colonne Clients */}
              <KanbanColumn
                id="clients"
                title="Clients"
                icon={Users}
                count={totauxDeColonne.clients}
                items={getFilteredItems('clients')}
                searchQuery={searchQueries.clients}
                onSearchChange={(q) => setSearchQuery('clients', q)}
                droppable={true}
                accepts={['site']}
                renderCard={(client) => (
                  <ClientCard
                    key={client.id}
                    client={client}
                    onManageAccess={handleManageAccess}
                    onClick={() => selectItem('client', client.id)}
                    isSelected={selection.type === 'client' && selection.id === client.id}
                  />
                )}
                emptyMessage="Aucun client"
                footerAction={
                  <button
                    style={footerButtonStyle}
                    onClick={() => setCreateClientModalOpen(true)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.color = '#1a1a1a';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d5cfc5';
                      e.currentTarget.style.color = '#64748b';
                    }}
                  >
                    <Plus size={14} />
                    Nouveau
                  </button>
                }
              />
            </>
          )}
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay dropAnimation={null}>
        {activeItem && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            border: '2px solid #1a1a1a',
            padding: '0.75rem',
            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.2)',
            minWidth: '220px',
            maxWidth: '280px',
            cursor: 'grabbing'
          }}>
            {/* Header avec logo/icône et type */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {activeItem.provider && (() => {
                const logoMap = {
                  'GOOGLE_ADS': googleAdsLogo,
                  'MICROSOFT_ADS': microsoftAdsLogo,
                  'FACEBOOK_ADS': metaAdsLogo,
                  'MATOMO': matomoLogo,
                  'PIANO_ANALYTICS': pianoLogo,
                  'PINTEREST_ADS': pinterestAdsLogo,
                  'TIKTOK_ADS': tiktokAdsLogo,
                  'OPENAI_ADS': chatgptAdsLogo,
                  'AWIN': awinLogo,
                  'PRESTASHOP': prestashopLogo
                };
                const logo = logoMap[activeItem.provider];
                return logo ? (
                  <img src={logo} alt="" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                ) : <Package size={18} style={{ color: '#64748b' }} />;
              })()}
              {activeItem.type === 'site' && <Globe size={18} style={{ color: '#64748b' }} />}
              {!activeItem.provider && activeItem.type !== 'site' && <Package size={18} style={{ color: '#64748b' }} />}
              <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600' }}>
                {activeItem.type === 'site' ? 'Site' : (providerLabels[activeItem.provider] || 'Compte')}
              </span>
            </div>
            {/* Nom */}
            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b' }}>
              {activeItem.name || 'Élément'}
            </div>
          </div>
        )}
      </DragOverlay>

      {/* Modals */}
      <ImportAccountsModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        connectors={connectors}
        existingAccounts={accounts}
        token={token}
        onImport={handleDataRefresh}
      />

      <ClientAccessModal
        isOpen={accessModalOpen}
        onClose={() => {
          setAccessModalOpen(false);
          setSelectedClientForAccess(null);
        }}
        client={selectedClientForAccess}
        token={token}
        onUpdate={handleDataRefresh}
      />

      <SetupWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        connectors={connectors}
        accounts={accounts}
        clients={clients}
        token={token}
        onComplete={handleDataRefresh}
      />

      <CreateSiteModal
        isOpen={createSiteModalOpen}
        onClose={() => setCreateSiteModalOpen(false)}
        token={token}
        onCreated={handleDataRefresh}
      />

      <CreateClientModal
        isOpen={createClientModalOpen}
        onClose={() => setCreateClientModalOpen(false)}
        token={token}
        onCreated={handleDataRefresh}
      />

      {/* Modal de sélection de connecteur */}
      {addConnectorModalOpen && ReactDOM.createPortal(
        <div
          onClick={() => setAddConnectorModalOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                <Plug size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Ajouter un connecteur
              </h2>
              <button
                onClick={() => setAddConnectorModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#64748b'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '0.9rem' }}>
              Sélectionnez le type de connecteur que vous souhaitez ajouter.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
              {/* Google Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  window.location.href = `${API_URL}/api/auth/google-ads?token=${token}&returnTo=kanban`;
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={googleAdsLogo} alt="Google Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Google Ads</span>
              </button>

              {/* Microsoft Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  window.location.href = `${API_URL}/api/auth/microsoft-ads?token=${token}&returnTo=kanban`;
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={microsoftAdsLogo} alt="Microsoft Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Microsoft Ads</span>
              </button>

              {/* Meta Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  window.location.href = `${API_URL}/api/auth/facebook-ads?token=${token}&returnTo=kanban`;
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={metaAdsLogo} alt="Meta Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Meta Ads</span>
              </button>

              {/* Pinterest Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  window.location.href = `${API_URL}/api/auth/pinterest-ads?token=${token}&returnTo=kanban`;
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={pinterestAdsLogo} alt="Pinterest Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Pinterest Ads</span>
              </button>

              {/* TikTok Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  window.location.href = `${API_URL}/api/auth/tiktok-ads?token=${token}&returnTo=kanban`;
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={tiktokAdsLogo} alt="TikTok Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>TikTok Ads</span>
              </button>

              {/* Google Analytics */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  window.location.href = `${API_URL}/api/auth/google-analytics?token=${token}&returnTo=kanban`;
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <GoogleAnalyticsLogo style={{ width: '32px', height: '32px' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Google Analytics</span>
              </button>

              {/* Google Search Console */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  window.location.href = `${API_URL}/api/auth/google-search-console?token=${token}&returnTo=kanban`;
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={googleSearchConsoleLogo} alt="Google Search Console" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b', textAlign: 'center' }}>Search Console</span>
              </button>

              {/* Google Tag Manager */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  window.location.href = `${API_URL}/api/auth/google-tag-manager?token=${token}&returnTo=kanban`;
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={tagManagerLogo} alt="Google Tag Manager" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b', textAlign: 'center' }}>Tag Manager</span>
              </button>

              {/* Matomo */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  onConnectorConnect('MATOMO'); // Connexion par formulaire : la page Connecteurs ouvre la modale
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={matomoLogo} alt="Matomo" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Matomo</span>
              </button>

              {/* Piano Analytics */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  onConnectorConnect('PIANO_ANALYTICS'); // Connexion par formulaire : la page Connecteurs ouvre la modale
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={pianoLogo} alt="Piano Analytics" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Piano Analytics</span>
              </button>

              {/* ChatGPT Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  onConnectorConnect('OPENAI_ADS'); // Connexion par clé API : la page Connecteurs ouvre la modale
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={chatgptAdsLogo} alt="ChatGPT Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>ChatGPT Ads</span>
              </button>

              {/* Awin (affiliation) */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  onConnectorConnect('AWIN'); // Connexion par clé API : la page Connecteurs ouvre la modale
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={awinLogo} alt="Awin" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Awin</span>
              </button>

              {/* PrestaShop (boutique) */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  onConnectorConnect('PRESTASHOP'); // Clé du webservice : la page Connecteurs ouvre la modale
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={prestashopLogo} alt="PrestaShop" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>PrestaShop</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </DndContext>
  );
};

/**
 * La table `PLANS` locale et son `getAccountLimit` sont partis avec le badge de comptage.
 *
 * Ils n'avaient que lui pour lecteur. C'était l'une des copies de la table des plans que le
 * CLAUDE.md signale comme repli — une limite recalculée côté client, donc susceptible de
 * diverger de celle que le serveur applique. Le compteur disparu, la copie n'avait plus de
 * raison d'être maintenue à jour, et une table de plans périmée est pire qu'absente : elle
 * annonce un quota que personne n'applique.
 *
 * Un compteur de comptes, si l'envie revient, se lit sur `limits.accounts.total`
 * (`/api/subscription/limits`) — la valeur que le serveur oppose réellement.
 */

// Composant wrapper avec le Provider
const KanbanView = ({
  token,
  onConnectorConnect,
  onConnectorDisconnect,
  onDataChange,
  clientFiltre = null
}) => {
  return (
    <KanbanProvider
      token={token}
      clientFiltre={clientFiltre}
    >
      <KanbanContent
        token={token}
        onConnectorConnect={onConnectorConnect}
        onConnectorDisconnect={onConnectorDisconnect}
        onDataChange={onDataChange}
      />
    </KanbanProvider>
  );
};

export default KanbanView;
